import { saveAs } from "file-saver";

// ============================================================
// Utilidad de exportación a Excel
//
// Genera un archivo CSV con BOM UTF-8 que Excel abre nativamente
// con soporte de acentos y caracteres en español. Usa separador
// ';' (estándar regional español/latinoamericano de Excel) para
// evitar conflictos cuando los datos contienen comas.
//
// Por qué CSV y no .xlsx: el repositorio no tiene la dependencia
// `xlsx` / `exceljs`. CSV con BOM cumple el requisito funcional
// (descargar y abrir en Excel) sin agregar peso al bundle.
// ============================================================

const CSV_SEPARATOR = ";";
const UTF8_BOM = "﻿";

const escapeCsvCell = (value) => {
  if (value === null || value === undefined) return "";
  let cell = String(value);

  if (cell.includes('"')) {
    cell = cell.replace(/"/g, '""');
  }
  if (
    cell.includes(CSV_SEPARATOR) ||
    cell.includes('"') ||
    cell.includes("\n") ||
    cell.includes("\r")
  ) {
    cell = `"${cell}"`;
  }
  return cell;
};

const buildFilename = (baseName) => {
  const stamp = new Date().toISOString().slice(0, 10);
  const safe = String(baseName || "export").replace(/[^a-zA-Z0-9_-]+/g, "_");
  return `${safe}_${stamp}.csv`;
};

/**
 * Exporta un arreglo de filas a un archivo CSV abierto por Excel.
 *
 * @param {Object} options
 * @param {Array<Object>} options.rows - Filas a exportar.
 * @param {Array<{ field: string, headerName: string, valueGetter?: (row: Object) => any }>} options.columns
 *   Definición de columnas. headerName se usa como cabecera.
 * @param {string} options.filename - Nombre base del archivo (sin extensión).
 *
 * Ejemplo:
 *   exportRowsToExcel({
 *     rows: employees,
 *     columns: [
 *       { field: "fullName", headerName: "Name",
 *         valueGetter: (r) => `${r.firstName} ${r.lastName}` },
 *       { field: "mail", headerName: "Email" },
 *     ],
 *     filename: "employees",
 *   });
 */
export const exportRowsToExcel = ({ rows, columns, filename }) => {
  if (!Array.isArray(rows) || !Array.isArray(columns)) {
    return;
  }

  const headerLine = columns
    .map((c) => escapeCsvCell(c.headerName ?? c.field))
    .join(CSV_SEPARATOR);

  const dataLines = rows.map((row) =>
    columns
      .map((c) => {
        const raw =
          typeof c.valueGetter === "function"
            ? c.valueGetter(row)
            : row?.[c.field];
        return escapeCsvCell(raw);
      })
      .join(CSV_SEPARATOR)
  );

  const csv = UTF8_BOM + [headerLine, ...dataLines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  saveAs(blob, buildFilename(filename));
};
